import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Camera, PlayArrow } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppState } from "../../../../../redux";
import {
  addDocumentCapture,
  addImageCapture as addParticipantCapture,
} from "../../../../../redux/videoAuthentication/actions";
import example1Mobile from "../../../../../assets/video_authentisierung/authentifizierung-1-mobile.png";
import example2Mobile from "../../../../../assets/video_authentisierung/authentifizierung-2-mobile.png";
import example1 from "../../../../../assets/video_authentisierung/authentifizierung-1.png";
import example2 from "../../../../../assets/video_authentisierung/authentifizierung-2.png";
import { CountDown } from "./CountDown";
import { NavControls } from "./NavControls";
import OnSiteAuthPopup from "./on-site-popup/OnSiteAuthPopup";
import { loggerService } from "../../../../../api";
import { LogLevelType } from "../../../../../models/enums/logLevelType.enum";
import { isMobileDevice } from "../../../../../utils/isMobileDevice";
import { isIpadDevice } from "../../../../../utils/isIpadDevice";
import { CaptureInstructions } from "./CaptureInstructions";

export const Capture: React.FC = () => {
  const { t } = useTranslation(["authCapture", "snackbars"]);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const isMobile = isMobileDevice();
  const isIpad = isIpadDevice();
  const isMobileOrIpad = Boolean(isMobile || isIpad);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement>(document.createElement("video"));

  const { captures, documentCaptures } = useSelector(
    (state: AppState) => state.videoAuthentication
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [timerStarted, setTimerStarted] = useState(false);
  const [modeCardIDCapture, setModeCardIDCapture] = useState(false);
  const [previewCapture, setPreviewCapture] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);

  const componentName = "Capture";
  const canvasWidthScreenWidthRatio = 0.7;

  // ─────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Only request camera when not showing instructions
    if (!showInstructions && videoRef.current) requestCameraStream();
    return () => stopCameraStream();
  }, [videoRef.current, isMobileOrIpad, showInstructions]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = cameraStream;
    if (cameraStream) video.play();
    return () => stopVideo();
  }, [cameraStream]);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (cameraStream && video && ctx && canvas && !video.paused && !video.ended) {
      const [start, stop] = drawMaskedVideoFrame(video, canvas, ctx);
      start();
      return stop;
    }
  }, [videoRef, canvasRef, cameraStream, modeCardIDCapture, isMobileOrIpad]);

  // ─────────────────────────────────────────────
  // CAMERA / VIDEO HANDLING
  // ─────────────────────────────────────────────
  const countdownToCapture = (): number =>
    process.env.REACT_APP_COUNTDOWN_AUTH_VIDEOCAPTURE
      ? Number(process.env.REACT_APP_COUNTDOWN_AUTH_VIDEOCAPTURE)
      : 10;

  const stopVideo = (): void => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.srcObject = null;
  };

  const stopCameraStream = (): void => {
    cameraStream?.getTracks().forEach((track) => track.stop());
    setCameraStream(null);
  };

  const stopVideoAndCameraStream = (): void => {
    try {
      stopVideo();
      stopCameraStream();
    } catch (err) {
      loggerService.addLog(
        `${componentName} - stopVideoAndCameraStream error: ${String(err)}`,
        LogLevelType.Error
      );
    }
  };

  const requestCameraStream = (): void => {
    if (cameraStream) return;
    const containerWidth =
      canvasRef.current?.parentElement?.getBoundingClientRect().width ?? 0;
    if (containerWidth === 0) return;

    const videoHeight = isMobileOrIpad
      ? window.innerHeight * 0.5
      : window.innerHeight * 0.65;
    navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: {
          facingMode: "user",
          width: containerWidth - 20,
          height: videoHeight,
        },
      })
      .then(
        (stream) => setCameraStream(stream),
        () =>
          enqueueSnackbar(t("events.authenticationCameraError", { ns: "snackbars" }), {
            variant: "error",
          })
      );
  };

  // ================================================================> Drawing on canvas
  /**
   * Function to draw masked video frame
   * @param {HTMLVideoElement} video - The video element.
   * @param {HTMLCanvasElement} canvas - The canvas element.
   * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
   * @returns {Array<Function>} - Start and stop functions for animation.
   */
  function drawMaskedVideoFrame(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ) {
    let requestId: number | undefined;

    /**
     * Function to animate the video frame.
     * Draws the video frame on the canvas with the mask.
     * @returns {void}
     */
    const animate = () => {
      if (video.videoWidth * video.videoHeight > 0) {
        if (isMobileOrIpad) {
          canvas.width = Math.round(canvasWidthScreenWidthRatio * video.videoWidth);
          canvas.height = Math.round(canvasWidthScreenWidthRatio * video.videoHeight);
          if (video.videoWidth > video.videoHeight) {
            canvas.width = Math.round(canvasWidthScreenWidthRatio * video.videoHeight);
            canvas.height = Math.round(canvasWidthScreenWidthRatio * video.videoWidth);
          }
        } else {
          canvas.width = Math.min(
            800,
            Math.round(canvasWidthScreenWidthRatio * screen.width)
          );
          canvas.height = Math.round((canvas.width / 16) * 9);
        }

        if (modeCardIDCapture) {
          drawWithDocumentMask(ctx, video, canvas);
        } else {
          drawWithParticipantMask(ctx, video, canvas);
        }
      }
      start();
    };

    // STEP 1 <==========================================> Capture the participant: STEP 1
    /**
     * Function to draw with participant mask
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
     * @param {HTMLVideoElement} video - The video element.
     * @param {HTMLCanvasElement} canvas - The canvas element.
     */
    function drawWithParticipantMask(
      ctx: CanvasRenderingContext2D,
      video: HTMLVideoElement,
      canvas: HTMLCanvasElement
    ) {
      // mirrored for draw of video
      ctx.setTransform(-1.0, 0, 0, 1, canvas.width, 0);
      ctx.drawImage(video, 0, 0);
      ctx.beginPath();

      let canvasWidth = 800;
      let canvasHeight = 450;

      if (isMobileOrIpad) {
        canvasHeight = Math.round(canvasWidthScreenWidthRatio * video.videoHeight);
        canvasWidth = Math.round(canvasWidthScreenWidthRatio * video.videoWidth);

        canvasHeight = Math.max(
          canvasWidthScreenWidthRatio * video.videoHeight,
          canvasWidthScreenWidthRatio * video.videoWidth
        );
        canvasWidth = Math.min(
          canvasWidthScreenWidthRatio * video.videoHeight,
          canvasWidthScreenWidthRatio * video.videoWidth
        );

        const faceRectWidth = Math.round(canvasWidth * 0.625);
        const faceRectHeight = Math.round(canvasHeight * 0.625);

        const faceRectLeft = Math.round(0.5 * (canvasWidth - faceRectWidth));
        const faceRectTop = Math.round(0.025 * canvasHeight);

        ctx.rect(faceRectLeft, faceRectTop, faceRectWidth, faceRectHeight);

        ctx.rect(
          faceRectLeft + faceRectWidth * 0.25,
          faceRectTop + faceRectHeight * 0.15,
          faceRectWidth * 0.5,
          faceRectHeight * 0.7
        );

        const documentRectHeigth = Math.round(canvasHeight * 0.3);
        const documentRectWidth = Math.round(canvasWidth * 0.6);

        const documentRectLeft = 0.5 * (canvasWidth - documentRectWidth);
        const documentRectTop = 0.675 * canvasHeight;

        ctx.rect(
          documentRectLeft,
          documentRectTop,
          documentRectWidth,
          documentRectHeigth
        );

        ctx.fillStyle = "#E7E9EB";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      } else {
        const faceRectWidth = 240;
        const faceRectHeight = 280;
        const faceRectLeft = 0.5 * (canvasWidth - faceRectWidth);
        const faceRectTop = 0.1 * (canvasHeight - faceRectHeight);

        ctx.ellipse(
          faceRectLeft + faceRectWidth * 0.5,
          faceRectTop + faceRectHeight * 0.5,
          faceRectWidth * 0.5,
          faceRectHeight * 0.5,
          0,
          0,
          2 * Math.PI
        );
        ctx.rect(faceRectLeft, faceRectTop, faceRectWidth, faceRectHeight);

        const documentRectWidth = 160;
        const documentRectHeigth = 100;

        const documentRectLeft = 100;
        const documentRectTop = 0.2 * (canvasHeight - documentRectHeigth);

        ctx.rect(
          documentRectLeft,
          documentRectTop,
          documentRectWidth,
          documentRectHeigth
        );

        ctx.fillStyle = "#E7E9EB";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      }

      ctx.clip();

      // CLEAR Image
      ctx.filter = "none";
      ctx.fillStyle = "#E7E9EB";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // mirrored for draw of video
      ctx.setTransform(-1.0, 0, 0, 1, canvasWidth, 0);
      ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight);
      ctx.strokeStyle = process.env.REACT_APP_PRIMARY_COLOR ?? "#2FA79E";
      ctx.stroke();
    }

    // STEP 2 <=============================================> Capture the document: STEP 2
    /**
     * Function to draw with document mask of the fullsize document capture.
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
     * @param {HTMLVideoElement} video - The video element.
     * @param {HTMLCanvasElement} canvas - The canvas element.
     */
    function drawWithDocumentMask(
      ctx: CanvasRenderingContext2D,
      video: HTMLVideoElement,
      canvas: HTMLCanvasElement
    ) {
      // mirrored for draw of video
      ctx.setTransform(-1.0, 0, 0, 1, canvas.width, 0);

      ctx.drawImage(video, 0, 0);
      ctx.beginPath();

      let canvasWidth = 800;
      let canvasHeight = 450;

      if (isMobileOrIpad) {
        canvasHeight = Math.round(canvasWidthScreenWidthRatio * video.videoHeight);
        canvasWidth = Math.round(canvasWidthScreenWidthRatio * video.videoWidth);

        canvasHeight = Math.max(
          canvasWidthScreenWidthRatio * video.videoHeight,
          canvasWidthScreenWidthRatio * video.videoWidth
        );
        canvasWidth = Math.min(
          canvasWidthScreenWidthRatio * video.videoHeight,
          canvasWidthScreenWidthRatio * video.videoWidth
        );

        const documentRectHeigth = Math.round(canvasHeight * 0.9);
        const documentRectWidth = Math.round((documentRectHeigth / 8.5) * 5.5);

        const documentRectLeft = Math.round(0.5 * (canvasWidth - documentRectWidth));
        const documentRectTop = Math.round(0.05 * canvasHeight);

        ctx.rect(
          documentRectLeft,
          documentRectTop,
          documentRectWidth,
          documentRectHeigth
        );

        const faceRectWidth = Math.round((documentRectWidth / 5.5) * 4);
        const faceRectHeight = Math.round((faceRectWidth / 4) * 3);
        const faceRectLeft = Math.round(0.45 * (canvasWidth - faceRectWidth));
        const faceRectTop = Math.round(0.1 * canvasHeight);

        ctx.rect(faceRectLeft, faceRectTop, faceRectWidth, faceRectHeight);

        ctx.fillStyle = "#E7E9EB";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      } else {
        const faceRectWidth = 230;
        const faceRectHeight = 300;
        const faceRectLeft = 0.2 * (canvasWidth - faceRectWidth);
        const faceRectTop = 0.5 * (canvasHeight - faceRectHeight);

        ctx.rect(faceRectLeft, faceRectTop, faceRectWidth, faceRectHeight);

        const documentRectWidth = 600;
        const documentRectHeigth = 380;

        const documentRectLeft = 0.5 * (canvasWidth - documentRectWidth);
        const documentRectTop = 0.5 * (canvasHeight - documentRectHeigth);

        ctx.rect(
          documentRectLeft,
          documentRectTop,
          documentRectWidth,
          documentRectHeigth
        );

        ctx.fillStyle = "#E7E9EB";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      }

      ctx.clip();

      // CLEAR Image
      ctx.filter = "none";
      ctx.fillStyle = "#E7E9EB";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // mirrored for draw of video
      ctx.setTransform(-1.0, 0, 0, 1, canvas.width, 0);
      ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight);
      ctx.strokeStyle = process.env.REACT_PRIMARY_COLOR ?? "#2FA79E";
      ctx.lineWidth = 3;
      ctx.stroke();
    }

    /**
     * Function to start the animation.
     * @returns {void}
     */
    const start = () => {
      requestId = requestAnimationFrame(animate);
    };

    /**
     * Function to stop the animation.
     * @returns {void}
     */
    const stop = () => {
      if (requestId) {
        cancelAnimationFrame(requestId);
        requestId = undefined;
      }
    };

    return [start, stop];
  }

  // ─────────────────────────────────────────────
  // CAPTURE HANDLING
  // ─────────────────────────────────────────────
  const handleCapture = (): void => {
    const capture = canvasRef.current?.toDataURL();
    if (capture) {
      setPreviewCapture(capture);
      stopVideoAndCameraStream();
      setIsDialogOpen(true);
    }
    setTimerStarted(false);
  };

  const handleRetake = (): void => {
    setPreviewCapture(null);
    setIsDialogOpen(false);
    requestCameraStream();
  };

  const handleConfirm = (): void => {
    if (!previewCapture) return;

    if (modeCardIDCapture) {
      dispatch(addDocumentCapture(previewCapture));
      setIsDialogOpen(false);
      setPreviewCapture(null);
      routeToNextStep();
    } else {
      dispatch(addParticipantCapture(previewCapture));
      setModeCardIDCapture(true);
      setShowInstructions(true);
      setCanProceed(false);
      setIsDialogOpen(false);
      setPreviewCapture(null);
    }
  };

  const handleContinueToCapture = (): void => {
    setShowInstructions(false);
  };

  const routeToNextStep = (): void => {
    stopVideoAndCameraStream();
    loggerService.addLog(`${componentName} - routeToNextStep`, LogLevelType.Info);
    history.push("?step=2");
  };

  const routeBack = (): void => {
    stopVideoAndCameraStream();
    loggerService.addLog(`${componentName} - routeBack`, LogLevelType.Info);
    history.push("?");
  };

  const handleProceed = (): void => {
    if (captures.length === 1 && documentCaptures.length === 1) {
      routeToNextStep();
    } else if (captures.length === 1 && documentCaptures.length === 0) {
      setModeCardIDCapture(true);
      setShowInstructions(true);
      setCanProceed(false);
    } else {
      routeBack();
    }
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  if (showInstructions) {
    return (
      <>
        <CaptureInstructions
          modeCardIDCapture={modeCardIDCapture}
          onContinue={handleContinueToCapture}
          isMobileOrIpad={isMobileOrIpad}
        />
        <OnSiteAuthPopup />
      </>
    );
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="center" margin="1.25rem">
          <canvas ref={canvasRef} style={{ width: "100%", maxWidth: 800 }} />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Grid
          container
          spacing={1}
          justifyContent={isMobileOrIpad ? "center" : "flex-end"}
          alignItems="center"
        >
          <Grid item xs={12} sm={isMobileOrIpad ? 12 : "auto"}>
            <Button
              id="camera-on"
              variant="contained"
              fullWidth={isMobileOrIpad}
              onClick={
                cameraStream === null ? requestCameraStream : stopVideoAndCameraStream
              }
              startIcon={<PlayArrow />}
              color={cameraStream === null ? "primary" : "secondary"}
              disabled={timerStarted}
            >
              {cameraStream === null
                ? t("shotCommands.cameraOn", { ns: "authCapture" })
                : t("shotCommands.cameraOff", { ns: "authCapture" })}
            </Button>
          </Grid>

          <Grid item xs={12} sm={isMobileOrIpad ? 12 : "auto"}>
            {cameraStream && !timerStarted ? (
              <Button
                fullWidth={isMobileOrIpad}
                id="start-shot"
                onClick={() => {
                  if (isMobileOrIpad) handleCapture();
                  else setTimerStarted(true);
                }}
                startIcon={<Camera />}
                variant="contained"
                color="primary"
              >
                {t("shotCommands.startShot", { ns: "authCapture" })}
              </Button>
            ) : null}
            {timerStarted ? (
              <CountDown
                seconds={countdownToCapture()}
                onZero={handleCapture}
                color="error"
                variant="h2"
                align="center"
              />
            ) : null}
          </Grid>
        </Grid>
      </Grid>

      <NavControls
        canProceed={canProceed}
        onProceed={handleProceed}
        showProceedButton={false}
      />
      <OnSiteAuthPopup />

      {/* Confirmation popup */}
      <Dialog
        open={isDialogOpen}
        onClose={handleRetake}
        fullWidth
        maxWidth="md"
        fullScreen={fullScreen}
      >
        <DialogTitle>{t("shotCommands.useShot", { ns: "authCapture" })}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} alignItems="flex-start" justifyContent="center">
            <Grid item xs={6} sm={6}>
              <Typography align="center" gutterBottom>
                {t("shotCommands.example", { ns: "authCapture" })}
              </Typography>
              <img
                src={
                  isMobileOrIpad
                    ? modeCardIDCapture
                      ? example2Mobile
                      : example1Mobile
                    : modeCardIDCapture
                      ? example2
                      : example1
                }
                alt="Example"
                style={{
                  width: "100%",
                  borderRadius: 4,
                  maxHeight: fullScreen ? "40vh" : "auto",
                  objectFit: "contain",
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6}>
              <Typography align="center" gutterBottom>
                {t("shotCommands.userShot", { ns: "authCapture" })}
              </Typography>
              {previewCapture ? (
                <img
                  src={previewCapture}
                  alt="Captured preview"
                  style={{
                    width: "100%",
                    borderRadius: 4,
                    maxHeight: fullScreen ? "40vh" : "auto",
                    objectFit: "contain",
                  }}
                />
              ) : null}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: 4 }}>
          <Grid
            container
            justifyContent={isMobileOrIpad ? "flex-start" : "flex-end"}
            spacing={1}
            direction={isMobileOrIpad ? "column" : "row"}
          >
            <Grid item style={{ order: isMobileOrIpad ? 2 : 1 }}>
              <Button
                onClick={handleRetake}
                color="secondary"
                variant="contained"
                style={{ width: isMobileOrIpad ? "100%" : "auto" }}
              >
                {t("shotCommands.retakeShot", { ns: "authCapture" })}
              </Button>
            </Grid>
            <Grid item style={{ order: isMobileOrIpad ? 1 : 2 }}>
              <Button
                onClick={handleConfirm}
                color="primary"
                variant="contained"
                style={{ width: isMobileOrIpad ? "100%" : "auto" }}
              >
                {t("shotCommands.useShotConfirm", { ns: "authCapture" })}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
